import useAuthStore, { isPreAuthOnboardingToken, isPreAuthToken, isUserToken } from '@/stores/authStore'
import { computed, watch } from 'vue'
import { useIntervalFn, watchOnce } from '@vueuse/core'
import { idbQueryStorage, queryClient } from '../plugins/vue-query'
import { trackPortalActivity } from '../utils/userActivity'
import { getDatadogRum } from '../plugins/datadog'
import type { EveItemErrorResponseBody } from '@/types/_general'
import type { AdaJwtClaims, TokenList } from '@/stores/authStore'
import { createApiError } from '@/utils/api'
import google from '../assets/google.svg'
import microsoft from '../assets/microsoft.svg'
import visma_net from '../assets/visma_net.svg'
import email from '../assets/email.svg'

export const OAUTH_QUERY_PARAMS = [
  'oauth',
  'login_hint',
  'user_id',
  'session_timeout',
  'end_session_on_close',
  'onboarding',
] as const

/** Reads the `?onboarding=<id>` hint from a URL, if present. */
function getOnboardingHint(rawUrl: string | null | undefined): string | null {
  if (rawUrl == null) return null
  try {
    return new URL(rawUrl, window.location.origin).searchParams.get('onboarding')
  } catch {
    return null
  }
}

export interface OAuthParams {
  returnUrl?: string
  loginHint?: string
  userId?: string
  sessionTimeout?: number
  endSessionOnClose?: string
}

export const authProviders = [
  {
    id: 'google',
    title: 'common.google',
    image: google,
    href: new URL(`authenticates/oauth2/google/${import.meta.env.VITE_APP_NAME}`, import.meta.env.VITE_APP_API_ROOT),
    featured: true,
  },
  {
    id: 'microsoft',
    title: 'common.microsoft',
    image: microsoft,
    href: new URL(`authenticates/oauth2/microsoft/${import.meta.env.VITE_APP_NAME}`, import.meta.env.VITE_APP_API_ROOT),
    featured: true,
  },
  {
    id: 'visma_net',
    title: 'common.visma_net',
    image: visma_net,
    href: new URL(`authenticates/oauth2/visma_net/${import.meta.env.VITE_APP_NAME}`, import.meta.env.VITE_APP_API_ROOT),
    featured: false,
  },
  {
    id: 'visma_connect',
    title: 'common.visma_connect',
    // Visma Connect has no logo of its own; the login page renders the name only.
    image: undefined,
    href: new URL(
      `authenticates/oauth2/visma_connect/${import.meta.env.VITE_APP_NAME}`,
      import.meta.env.VITE_APP_API_ROOT
    ),
    featured: false,
  },
  {
    id: 'email',
    title: 'common.email',
    image: email,
    featured: true,
  },
] as const

export type AuthProvider = (typeof authProviders)[number]['id']

function dec2hex(dec: number) {
  return dec.toString(16).padStart(2, '0')
}

async function sha256(plain: string) {
  return await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain))
}

function base64urlencode(str: ArrayBuffer) {
  return btoa(String.fromCharCode(...new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

async function createCodeVerifier() {
  const array = new Uint32Array(56 / 2)
  window.crypto.getRandomValues(array)
  const codeVerifier = Array.from(array, dec2hex).join('')
  const hashed = await sha256(codeVerifier)
  return {
    codeVerifier,
    codeChallenge: base64urlencode(hashed),
    codeChallengeMethod: 'S256',
  }
}

function parseAuthError(body: string | { error: unknown } | EveItemErrorResponseBody): string {
  if (typeof body === 'string') return body
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  if ('error' in body && body.error != null) return String(body.error)
  if ('_error' in body) {
    if (typeof body._error === 'string') return body._error
    if (body._error?.message != null) return body._error.message
  }
  return 'Unknown error'
}

export function cleanOAuthParamsFromUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, window.location.origin)
    for (const param of OAUTH_QUERY_PARAMS) {
      url.searchParams.delete(param)
    }
    return url.pathname + url.search + url.hash
  } catch {
    return rawUrl
  }
}

export async function handleAuthCodeCallback(code: string, state: string): Promise<string> {
  const authStore = useAuthStore()
  // The session is consumed only after a successful exchange: a mistyped one-time code must leave it
  // in place, so the next attempt with the right code still has its verifier and session choice.
  const authSession = authStore.findAuthSession(state)
  if (authSession == null) throw new Error('Auth session not found')

  const res = await fetch(new URL('authenticates/challenge-code', import.meta.env.VITE_APP_API_ROOT), {
    method: 'POST',
    body: JSON.stringify({
      code,
      code_verifier: authSession.codeVerifier,
      application: import.meta.env.VITE_APP_NAME,
    }),
    headers: { 'Content-Type': 'application/json' },
  })

  let body: string | { token_list: TokenList } | { error?: unknown; _error?: { message?: string } } = await res.text()
  try {
    body = JSON.parse(body)
  } catch {
    /* empty */
  }
  if (!res.ok || typeof body === 'string' || !('token_list' in body)) {
    throw new Error(parseAuthError(body as Parameters<typeof parseAuthError>[0]), { cause: body })
  }

  authStore.popAuthSession(state)

  const useSessionStorage = authSession.endSessionOnClose === 'true'

  authStore.setTokenList(body.token_list, {
    storage: useSessionStorage ? 'session' : 'persistent',
    sessionTimeoutMinutes: authSession.sessionTimeout,
  })

  trackPortalActivity('Authentication', 'login')

  if (authSession.userId != null) {
    const matchingToken = authStore.tokensList.find(
      (t) => isUserToken(t.payload) && t.payload.u_id === authSession.userId
    )
    if (matchingToken != null) {
      await idbQueryStorage.clear()
      authStore.selectToken(matchingToken.token)
      await queryClient.invalidateQueries({ queryKey: ['get', '/user--extended-data'] })
    }
  }

  const onboardingHint = getOnboardingHint(authSession.redirectUrl)
  if (onboardingHint != null) {
    const matchingOnboardingToken = authStore.tokensList.find(
      (t) => isPreAuthOnboardingToken(t.payload) && t.payload.onb_id === onboardingHint
    )
    if (matchingOnboardingToken != null) authStore.selectToken(matchingOnboardingToken.token)
  }

  return cleanOAuthParamsFromUrl(authSession.redirectUrl ?? '/')
}

/**
 * Refresh this long before `exp`, so ordinary requests rarely leave with an expired token (the API
 * answers 419). Legacy ada-ui uses 30 s.
 */
export const REFRESH_SAFETY_MARGIN_MS = 20_000

/** True when the token's `exp` is within the safety margin (or already passed). Used by utils/api.ts onRequest. */
export function tokenNeedsRefresh(payload: AdaJwtClaims | null | undefined): boolean {
  if (payload == null || !('exp' in payload) || typeof payload.exp !== 'number') return false
  return payload.exp * 1000 - REFRESH_SAFETY_MARGIN_MS < Date.now()
}

/**
 * Wait (max 5 s) for the token another tab — or the lock holder in this tab — stores: persistent bucket
 * via the `storage` event, session bucket via useAuthTabSync. Shared by the no-lock branch of refreshToken
 * and the 202 branch; whichever side settles first tears the other down.
 */
function waitForSyncedToken(authStore: ReturnType<typeof useAuthStore>): Promise<string | null> {
  // Whichever side wins, the other is torn down: no watcher left behind after a timeout, no timer after a sync.
  return new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => {
      stopWatching()
      resolve(null)
    }, 5 * 1000)
    const stopWatching = watchOnce(
      () => authStore.selectedToken?.token,
      () => {
        clearTimeout(timer)
        console.log('Got new token')
        resolve(authStore.selectedToken?.token ?? null)
      }
    )
  })
}

export async function refreshToken(): Promise<string | null> {
  const authStore = useAuthStore()

  return await navigator.locks.request('refresh_token', { ifAvailable: true }, async (lock) => {
    if (authStore.selectedToken?.payload == null || !('exp' in authStore.selectedToken.payload)) return null
    console.log('Refreshing token...')

    // we don't have the lock, so someone else is refreshing the token
    if (lock == null) {
      // wait for selectedToken to change or exp for selected token is in the future
      if (authStore.selectedToken.payload.exp * 1000 > Date.now()) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return authStore.selectedToken.token ?? null
      }
      return waitForSyncedToken(authStore)
    }

    const sentToken = authStore.selectedToken.token
    try {
      const res = await fetch(new URL('authenticates/refresh-token', import.meta.env.VITE_APP_API_ROOT), {
        method: 'POST',
        headers: { 'x-api-key': sentToken },
      })

      if (res.status === 202) {
        // "Token was recently refreshed": another refresh on this chain succeeded within the last 5 s. The
        // body is an error body without a token — never store it (ADA-4556, M2). The new token comes from
        // the tab that refreshed (storage event / useAuthTabSync): use it if it is already here, else wait.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- a sibling tab may have cleared the bucket during the fetch
        const current = authStore.selectedToken?.token ?? sentToken
        return current !== sentToken ? current : await waitForSyncedToken(authStore)
      }

      if (!res.ok) {
        console.error('Failed to refresh token', `${res.status} ${res.statusText}`)
        if (res.status === 401 || res.status === 403) {
          // The server's verdict on this chain — the ONE place a refresh signs the tab out (ADA-4410 rule).
          // The body goes through the app's error factory (EveError carries `_error.message`); facet only.
          const err = createApiError(await res.json().catch(() => undefined), res.status)
          getDatadogRum()?.addAction('forced_logout', { reason: err.message, http_status: res.status })
          authStore.clearAllTokens()
        } else {
          // 5xx, 429 …: the token was not judged. Keep it; the caller's request fails and may be retried later.
          getDatadogRum()?.addAction('token_refresh_unavailable', { cause: `http_${res.status}` })
        }
        return null
      }

      const body = (await res.json()) as { token?: unknown } | null
      if (body == null || typeof body.token !== 'string' || body.token === '') {
        getDatadogRum()?.addAction('token_refresh_unavailable', { cause: 'invalid_body' })
        return null
      }
      authStore.updateCurrentToken({ token: body.token })
      return body.token
    } catch {
      // Network error: no verdict, keep the token (as before — but utils/api.ts no longer signs out on null).
      getDatadogRum()?.addAction('token_refresh_unavailable', { cause: 'network' })
      return null
    }
  })
}

export function useAuth() {
  const authStore = useAuthStore()

  async function signInWithRedirect(providerId: AuthProvider, params?: OAuthParams) {
    const provider = authProviders.find((p) => p.id === providerId)

    if (provider == null) throw new Error('Provider not found')

    if (!('href' in provider)) {
      throw new Error(`The provider "${providerId}" is not configured for redirection`)
    }

    const { codeVerifier, codeChallenge, codeChallengeMethod } = await createCodeVerifier()
    const state = authStore.addAuthSession(codeVerifier, params?.returnUrl, {
      userId: params?.userId,
      sessionTimeout: params?.sessionTimeout,
      endSessionOnClose: params?.endSessionOnClose,
    })

    const url = new URL(provider.href)
    url.searchParams.set('code_challenge', codeChallenge)
    url.searchParams.set('code_challenge_method', codeChallengeMethod)
    url.searchParams.set('state', state)

    if (params?.loginHint) {
      url.searchParams.set('login_hint', params.loginHint)
    }
    if (params?.userId) {
      url.searchParams.set('user_id', params.userId)
    }
    if (params?.sessionTimeout != null) {
      url.searchParams.set('session_timeout', String(params.sessionTimeout))
    }
    if (params?.endSessionOnClose) {
      url.searchParams.set('end_session_on_close', params.endSessionOnClose)
    }

    window.location.href = url.href
  }

  function signOut() {
    trackPortalActivity('top_bar', 'logout')
    authStore.clearAllTokens()
  }

  const { pause: pauseSessionCheck, resume: resumeSessionCheck } = useIntervalFn(
    () => {
      if (authStore.isSessionExpired) {
        signOut()
        pauseSessionCheck()
      }
    },
    30000,
    { immediate: false }
  )

  watch(
    () => authStore.sessionEnd,
    (sessionEnd) => {
      if (sessionEnd != null) {
        resumeSessionCheck()
      } else {
        pauseSessionCheck()
      }
    },
    { immediate: true }
  )

  function switchToSignedInByToken() {
    if (!isUserToken(authStore.selectedToken?.payload) || authStore.selectedToken.payload.signed_in_by_u_id == null) {
      console.warn('switchToSignedInByToken can only be used when a token with signed_in_by_u_id is selected')
      if (authStore.tokensList[0] != null) authStore.selectToken(authStore.tokensList[0].token)
      return
    }

    const signerInUserId = authStore.selectedToken.payload.signed_in_by_u_id
    const signerInTokenIdx = authStore.tokensList.findIndex(
      (t) => isUserToken(t.payload) && t.payload.u_id === signerInUserId
    )

    if (signerInTokenIdx > -1 && authStore.tokensList[signerInTokenIdx] != null) {
      authStore.selectToken(authStore.tokensList[signerInTokenIdx].token)
    } else if (authStore.tokensList[0] != null) {
      authStore.selectToken(authStore.tokensList[0].token)
    }
  }

  const isPreAuthenticated = computed(
    () => authStore.selectedToken != null && isPreAuthToken(authStore.selectedToken.payload)
  )
  const isAuthenticated = computed(() => authStore.selectedToken != null && !isPreAuthenticated.value)

  return {
    signInWithRedirect,
    signOut,
    switchToSignedInByToken,
    isPreAuthenticated,
    isAuthenticated,

    isSolicitor: computed(
      () => isUserToken(authStore.selectedToken?.payload) && authStore.selectedToken.payload.solicitor === true
    ),
    isSystemUser: computed(
      () => isUserToken(authStore.selectedToken?.payload) && authStore.selectedToken.payload.system_user === true
    ),

    selectedToken: computed(() => authStore.selectedToken),
    tokensList: computed(() => authStore.tokensList),
  }
}
